import type { Permission } from '@ctrl/db';
import {
  Building2,
  Clock,
  CreditCard,
  Globe,
  type LucideIcon,
  MapPin,
  MessageCircle,
  Newspaper,
  Palette,
  Percent,
  Radio,
  Receipt,
  Scale,
  ShieldCheck,
  Store,
  Truck,
  Upload,
  UserCog,
  UserRound,
  Users,
} from 'lucide-react';

/**
 * The settings sections, ported from ctrl-saas's settings index.
 *
 * `permission` mirrors the write gate of the tables behind each screen, and
 * every one of those routes 404s without it. Only `owner` holds all of them, so
 * without the filter a budtender would be shown a grid of cards that dead-end —
 * the same reasoning ctrl-saas gives for filtering them.
 */

export interface SettingsSection {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  permission?: Permission;
  /** Grouping for the dialog; ctrl-saas presented one flat grid. */
  group: 'Store' | 'Operations' | 'Storefront' | 'Account';
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    key: 'store',
    title: 'Store details',
    description: 'Trading name, contact details, address, timezone and currency.',
    icon: Building2,
    permission: 'settings.manage',
    group: 'Store',
  },
  {
    key: 'team',
    title: 'Team members',
    description: 'Invite staff, set roles and grant floor capabilities.',
    icon: Users,
    permission: 'members.manage',
    group: 'Store',
  },
  {
    key: 'locations',
    title: 'Locations',
    description: 'Stores, warehouses and hubs, their hours and licences.',
    icon: MapPin,
    permission: 'locations.manage',
    group: 'Store',
  },
  {
    key: 'delivery-zones',
    title: 'Delivery zones',
    description: 'ZIP coverage, fees, minimums and delivery time windows.',
    icon: Truck,
    permission: 'delivery.manage',
    group: 'Operations',
  },
  {
    key: 'vehicles',
    title: 'Vehicles',
    description: 'Delivery fleet, plates, insurance and value caps.',
    icon: Truck,
    permission: 'fleet.manage',
    group: 'Operations',
  },
  {
    key: 'taxes',
    title: 'Taxes & duties',
    description: 'Excise, cannabis and sales tax rates and how they are shown.',
    icon: Percent,
    permission: 'taxes.manage',
    group: 'Operations',
  },
  {
    key: 'fees',
    title: 'Fees',
    description: 'Service charges and surcharges added on top of the goods.',
    icon: Receipt,
    permission: 'settings.manage',
    group: 'Operations',
  },
  {
    key: 'limits',
    title: 'Purchase limits',
    description: 'How much of each category a customer may buy in a window.',
    icon: Scale,
    permission: 'compliance.manage',
    group: 'Operations',
  },
  {
    key: 'pickup',
    title: 'Pickup times',
    description: 'The collection slots shoppers can book at checkout.',
    icon: Clock,
    permission: 'locations.manage',
    group: 'Operations',
  },
  {
    key: 'channels',
    title: 'Sales channels',
    description: 'Web, counter and kiosk, and which catalog each one shows.',
    icon: Radio,
    permission: 'settings.manage',
    group: 'Operations',
  },
  {
    key: 'metrc',
    title: 'Metrc reporting',
    description: 'Connect state track-and-trace credentials per licence.',
    icon: ShieldCheck,
    permission: 'compliance.manage',
    group: 'Operations',
  },
  {
    key: 'branding',
    title: 'Branding',
    description: 'Storefront colours, typography, corner radius, spacing and layout.',
    icon: Palette,
    permission: 'cms.manage',
    group: 'Storefront',
  },
  {
    key: 'storefront',
    title: 'Storefront',
    description: 'Age gate, announcement bar, search listing and social links.',
    icon: Store,
    permission: 'settings.manage',
    group: 'Storefront',
  },
  {
    key: 'chat',
    title: 'Live chat',
    description: 'Turn the storefront chat widget on and set its greeting and hours.',
    icon: MessageCircle,
    permission: 'settings.manage',
    group: 'Storefront',
  },
  {
    key: 'blog',
    title: 'Blog',
    description: 'Show the news section on your storefront and name what readers see.',
    icon: Newspaper,
    permission: 'cms.manage',
    group: 'Storefront',
  },
  {
    key: 'domains',
    title: 'Web address',
    description: 'Your cannactrl subdomain and any custom domains.',
    icon: Globe,
    permission: 'settings.manage',
    group: 'Storefront',
  },
  {
    key: 'import',
    title: 'Import data',
    description: 'Bring products, packages and customers in from a spreadsheet.',
    icon: Upload,
    permission: 'catalog.manage',
    group: 'Operations',
  },
  {
    key: 'account',
    title: 'Account',
    description: 'Workspace name, avatar and ownership.',
    icon: UserCog,
    permission: 'settings.manage',
    group: 'Account',
  },
  {
    key: 'profile',
    title: 'Your profile',
    description: 'Email, password, connected accounts and passkeys.',
    icon: UserRound,
    // Deliberately ungated: your own profile is always yours to edit.
    group: 'Account',
  },
  {
    key: 'billing',
    title: 'Plan & billing',
    description: 'Subscription, invoices and payment method.',
    icon: CreditCard,
    permission: 'billing.manage',
    group: 'Account',
  },
];

export const SETTINGS_GROUP_ORDER = ['Store', 'Operations', 'Storefront', 'Account'] as const;
